import { useEffect, useCallback, useMemo, useRef } from 'react';
import {
  eventTarget,
  getEnabledElementByViewportId,
  triggerEvent,
} from '@cornerstonejs/core';
import { Enums, annotation as csAnnotation } from '@cornerstonejs/tools';
import { useDispatch, useSelector, useStore } from 'react-redux';
import {
  completeMeasurement,
  addSimpsonResult,
  removeSimpsonResult,
  addSpectralDopplerResult,
  removeSpectralDopplerResult,
  selectActiveMeasurement,
  selectMeasurementStatus,
  selectSimpsonResults,
  selectSpectralDopplerResults,
  selectAnnotationToMeasurement,
  selectSelectedAnnotationUID,
  updateHydratedMeasurement,
  type PendingAnnotationMeta,
} from '@/state/slices/measurementSession';
import { useGetWorksheetCanonicalMeasurementsQuery } from '@/state/slices/options';
import { getBaseAnnotationStyle } from '@/features/study-detail/Viewer/utils/annotationStyle';
import {
  calculateSimpsonVolume,
  getContourPointsFromAnnotation,
  CornerstoneAnnotation,
} from '@/utils/simpsonMethod';
import {
  normalizeUnit,
  getAnnotationTypeForToolType,
  SIMPSON_TOOL_NAME,
} from '@/utils/measurementToolMapping';
import {
  SPECTRAL_DOPPLER_TOOL_NAMES,
  type SpectralDopplerToolVariant,
  type SpectralDopplerValues,
  type SpectralDopplerRole,
  type SpectralCalibration,
} from '@/features/study-detail/Viewer/tools/SpectralDoppler';
import {
  commitHistoryStep,
  isApplyingHistory,
} from '@/features/study-detail/history/historyStore';
import { createReshapeStep } from '@/features/study-detail/history/annotationSteps';
import {
  cloneAnnotation,
  createCreateStep,
  annotationName,
} from '@/features/study-detail/history/annotationLifecycle';
import {
  sameAnnotationData,
  snapshotAllAnnotations,
  snapshotAnnotationData,
  type AnnotationDataSnapshot,
} from '@/features/study-detail/history/annotationSnapshot';
import {
  getWorksheetSnapshot,
  type WorksheetSnapshot,
} from '@/features/study-detail/history/worksheetApplier';
import {
  registerPaneLocation,
  unregisterPane,
} from '@/features/study-detail/history/viewerNavigation';
import {
  sameSessionResults,
  snapshotSessionResults,
  type SessionResultsSnapshot,
} from '@/features/study-detail/history/sessionResults';
import type { RootState } from '@/state/store';
import { triggerAllViewports } from '../utils/triggerAllViewports';
import {
  buildAnnotationPoints,
  hasContourGeometry,
  isBackendValidAnnotation,
} from '../utils/annotationPayload';

const SPECTRAL_TOOL_NAME_TO_VARIANT: Record<
  string,
  SpectralDopplerToolVariant
> = {
  [SPECTRAL_DOPPLER_TOOL_NAMES.PROBE]: 'probe',
  [SPECTRAL_DOPPLER_TOOL_NAMES.SEGMENT]: 'segment',
  [SPECTRAL_DOPPLER_TOOL_NAMES.VTI]: 'vti',
};

/** Primary value + unit per tool variant: peak velocity for probe/segment,
 *  VTI (cm) for the trace.
 *
 *  The velocity persists in the canonical measurement's unit — e' rows are
 *  defined in cm/s (precision 1), so hardcoding m/s wrote 0.049 into a row
 *  whose worksheet displays cm/s. Unknown/absent unit falls back to m/s. */
function extractPrimaryValue(
  variant: SpectralDopplerToolVariant,
  values: SpectralDopplerValues,
  canonicalUnit?: string | null
): { value: number; unit: string; role: SpectralDopplerRole } | null {
  if (variant === 'vti' && values.vti_cm != null) {
    return { value: values.vti_cm, unit: 'cm', role: 'vti' };
  }
  if (values.peakV_cmS != null) {
    // Peak velocity is reported as a magnitude — the derived value is signed
    // (baseline convention; a peak below the baseline derives negative), but the
    // worksheet/AI report it positive. Without abs, dragging a probe whose peak
    // sits on the negative side of the baseline flips the value negative.
    const peakCmS = Math.abs(values.peakV_cmS);
    if (canonicalUnit && normalizeUnit(canonicalUnit) === 'cm/s') {
      return { value: peakCmS, unit: 'cm/s', role: 'peakV' };
    }
    return { value: peakCmS / 100, unit: 'm/s', role: 'peakV' };
  }
  return null;
}

interface AnnotationCompletedEventDetail {
  annotation: {
    annotationUID: string;
    data: {
      cachedStats?: Record<
        string,
        {
          length?: number;
          area?: number;
          perimeter?: number;
          angle?: number;
        }
      >;
      contour?: {
        polyline: [number, number, number][];
      };
      handles?: {
        points?: [number, number, number][];
      };
    };
    metadata: {
      toolName: string;
    };
  };
}

// An invalid annotation would 400 the atomic save — keep the value, drop it.
function guardAnnotationMeta(
  meta: PendingAnnotationMeta
): PendingAnnotationMeta | undefined {
  if (isBackendValidAnnotation(meta)) return meta;
  // eslint-disable-next-line no-console -- surface payloads the backend would reject
  console.warn('[MeasurementCapture] Dropping invalid annotation', meta);
  return undefined;
}

/**
 * Mutates the annotation in place so Cornerstone's PlanarFreehandROITool
 * text box can read Simpson-derived values. Paired with
 * customFreehandROIGetTextLines in useSharedToolState.ts.
 */
function writeSimpsonStatsToAnnotation(
  annotation: { data?: unknown } | null | undefined,
  result: {
    volume: number;
    volumeUnit: string;
    longAxisLength: number;
    longAxisUnit: string;
  }
): void {
  if (!annotation?.data || typeof annotation.data !== 'object') return;
  const data = annotation.data as Record<string, unknown>;
  data.simpsonStats = {
    volume: result.volume,
    volumeUnit: result.volumeUnit,
    longAxisLength: result.longAxisLength,
    longAxisUnit: result.longAxisUnit,
  };
}

/**
 * Hook that listens for Cornerstone.js annotation completion events
 * and dispatches measurement values to Redux when a measurement is active.
 *
 * @param viewportId - The viewport ID to listen for events
 * @param currentFrame - Current frame index (for multi-frame DICOMs)
 * @param pixelSpacing - Pixel spacing in mm (from DICOM metadata)
 * @param dicomUuid - DICOM UUID to scope annotations to specific DICOM
 * @param activeViewportId - Currently active viewport (where user is drawing)
 * @param activeTool - The viewer's active tool (Simpson traces volumes without a row)
 * @param renderingEngine - Rendering engine for re-rendering after annotation removal
 */
export function useMeasurementCapture(
  viewportId: string | undefined,
  currentFrame: number,
  pixelSpacing?: number,
  dicomUuid?: string,
  activeViewportId?: string | null,
  activeTool?: string
): void {
  const dispatch = useDispatch();
  // Read at mouse-down / mouse-up time (not render time) so the step sees the
  // results a tool dispatched mid-drag even before React re-rendered.
  const reduxStore = useStore<RootState>();
  const activeMeasurement = useSelector(selectActiveMeasurement);
  const measurementStatus = useSelector(selectMeasurementStatus);
  const simpsonResults = useSelector(selectSimpsonResults);
  const spectralDopplerResults = useSelector(selectSpectralDopplerResults);
  const annotationToMeasurement = useSelector(selectAnnotationToMeasurement);

  // Canonical unit per uuid — spectral values persist in the canonical's unit
  // (see extractPrimaryValue).
  const { data: canonicalMeasurements } =
    useGetWorksheetCanonicalMeasurementsQuery();
  const canonicalUnitByUuid = useMemo(() => {
    const map = new Map<string, string | null>();
    canonicalMeasurements?.forEach((c) => map.set(c.uuid, c.unit));
    return map;
  }, [canonicalMeasurements]);

  // Mirror the selected annotation UID in a ref so the mouse-up adopt handler
  // (a stable useCallback) can migrate the selection to the converted UID
  // without re-subscribing on every selection change.
  const selectedAnnotationUID = useSelector(selectSelectedAnnotationUID);
  const selectedAnnotationUIDRef = useRef<string | null>(selectedAnnotationUID);
  selectedAnnotationUIDRef.current = selectedAnnotationUID;

  // Track annotation UID when ANNOTATION_COMPLETED fires before cachedStats are ready.
  // ANNOTATION_MODIFIED with changeType 'StatsUpdated' will pick it up.
  const pendingAnnotationRef = useRef<string | null>(null);

  // Pending AI adoption: collect the latest stats during a drag of an
  // ai-hydrated-* annotation, then apply the UID conversion on MOUSE_UP.
  // Converting mid-drag removes the annotation Cornerstone is actively
  // manipulating and breaks subsequent drag events.
  const pendingAdoptRef = useRef<{
    oldUID: string;
    canonicalMeasurement: string;
    value: number;
    unit: string;
    points: [number, number, number][];
    /** FREEHAND for volume/area contours, POINT for Doppler probes — so the
     *  folded measurement keeps its type; omitted for linear (row keeps its
     *  existing type). */
    type?: 'FREEHAND' | 'POINT';
  } | null>(null);

  // Whether the primary mouse button is currently pressed. Freehand tools
  // finalize a reshape and fire ANNOTATION_MODIFIED slightly AFTER the document
  // mouseup, so the adopt gets stashed too late for the mouseup handler to see
  // it — needing a second click. When the stash lands while the mouse is
  // already up, we apply the adopt right away instead.
  const mouseDownRef = useRef(false);

  // AP-773 (R6): this pane reports what it shows so an undo can tell whether
  // its step's DICOM/frame is already on screen.
  useEffect(() => {
    if (!viewportId || !dicomUuid) return undefined;
    registerPaneLocation(viewportId, { dicomUuid, frame: currentFrame });
    return () => unregisterPane(viewportId);
  }, [viewportId, dicomUuid, currentFrame]);

  // AP-773 (A2): pre-drag state, captured on mouse-down so that mouse-up can
  // commit one history step per drag holding the annotation's before/after
  // data and the worksheet rows as they were before the drag. Every pane's
  // hook instance sees the same document events; only the pane under the
  // pointer at mouse-down (`owner`) commits, so a drag yields one step.
  const dragSnapshotRef = useRef<{
    owner: boolean;
    annotations: Map<string, AnnotationDataSnapshot>;
    worksheet: WorksheetSnapshot | null;
    /** Redux state at mouse-down — sibling results are read from it per UID. */
    reduxState: RootState;
    modified: Set<string>;
    /** UIDs whose ANNOTATION_COMPLETED fired during this drag (A1). */
    completed: Set<string>;
    /** Deferred commit handle (set at mouseup, cleared when it runs). */
    commitTimer: ReturnType<typeof setTimeout> | null;
  } | null>(null);
  // Ref to the adopt-applying callback so the modified handler can invoke it
  // without depending on it (assigned just below where it's defined).
  const applyPendingAdoptRef = useRef<() => void>(() => {});

  // Bug fix: Lock capture to this viewport when measurement starts while mouse is here.
  // The live activeViewportId check is hover-based and can flip to null/other viewport
  // before ANNOTATION_COMPLETED fires (e.g. user draws then moves mouse away).
  // Once locked, this viewport keeps capture ownership for the full measurement.
  const isCaptureViewportRef = useRef(false);

  useEffect(() => {
    if (measurementStatus === 'awaiting' && activeViewportId === viewportId) {
      isCaptureViewportRef.current = true;
    }
    if (measurementStatus !== 'awaiting') {
      isCaptureViewportRef.current = false;
    }
  }, [measurementStatus, activeViewportId, viewportId]);

  // A traced contour's Simpson volume (label + disks), for the armed row if any.
  const recordSimpsonVolume = useCallback(
    (
      traced: AnnotationCompletedEventDetail['annotation'],
      row?: { reportMeasurementClassUuid: string; measurementLabel: string }
    ): number | null => {
      try {
        const contourPoints = getContourPointsFromAnnotation(
          traced as unknown as CornerstoneAnnotation
        );
        if (contourPoints.length < 10) return null;
        const simpsonResult = calculateSimpsonVolume(contourPoints, {
          numberOfDisks: 20,
          pixelSpacing: pixelSpacing ?? 1,
        });
        writeSimpsonStatsToAnnotation(traced, simpsonResult);
        dispatch(
          addSimpsonResult({
            ...simpsonResult,
            annotationUID: traced.annotationUID,
            frameIndex: currentFrame,
            dicomUuid,
            ...row,
          })
        );
        return simpsonResult.volume;
      } catch {
        return null;
      }
    },
    [pixelSpacing, currentFrame, dicomUuid, dispatch]
  );

  const handleAnnotationCompleted = useCallback(
    (event: Event) => {
      // AP-773 (A1): every completion is a candidate step, linked or not,
      // regardless of which pane captures the measurement value below.
      {
        const uid = (event as CustomEvent<AnnotationCompletedEventDetail>)
          .detail?.annotation?.annotationUID;
        if (uid && !isApplyingHistory()) {
          dragSnapshotRef.current?.completed.add(uid);
        }
      }
      // Toolbar Simpson, no row armed: a volume with disks that fills no row.
      const traced = (event as CustomEvent<AnnotationCompletedEventDetail>)
        .detail?.annotation;
      if (
        activeTool === SIMPSON_TOOL_NAME &&
        !(activeMeasurement && measurementStatus === 'awaiting') &&
        dragSnapshotRef.current?.owner &&
        traced?.metadata?.toolName === 'PlanarFreehandROI'
      ) {
        recordSimpsonVolume(traced);
        return;
      }

      // Bug fix (Bug 3): Use the locked ref instead of live activeViewportId.
      // isCaptureViewportRef is set when measurement starts with mouse over this viewport
      // and stays set until the measurement leaves 'awaiting' state.
      if (!isCaptureViewportRef.current) {
        return;
      }

      // Only capture if we're actively awaiting a measurement
      if (!activeMeasurement || measurementStatus !== 'awaiting') {
        return;
      }

      const customEvent = event as CustomEvent<AnnotationCompletedEventDetail>;
      const { annotation: completedAnnotation } = customEvent.detail;

      if (!completedAnnotation?.data) {
        return;
      }

      const toolName = completedAnnotation.metadata?.toolName;
      let value: number | null = null;

      // Handle SPECTRAL DOPPLER tools (probe / segment / VTI) — always dispatched
      // as a sibling result so the Report can auto-populate all derived fields.
      const spectralVariant = toolName
        ? SPECTRAL_TOOL_NAME_TO_VARIANT[toolName]
        : undefined;
      if (spectralVariant && dicomUuid) {
        const data = completedAnnotation.data as unknown as {
          spectralValues?: SpectralDopplerValues;
          spectralCalibration?: SpectralCalibration;
          spectralPixelPoints?: [number, number][];
        };
        if (data?.spectralValues && data?.spectralCalibration) {
          const primary = extractPrimaryValue(
            spectralVariant,
            data.spectralValues,
            canonicalUnitByUuid.get(activeMeasurement.canonicalMeasurement) ??
              activeMeasurement.unit
          );
          const primaryBinding =
            activeMeasurement &&
            activeMeasurement.toolType &&
            activeMeasurement.toolType.startsWith('spectral-doppler-')
              ? {
                  reportMeasurementClassUuid:
                    activeMeasurement.canonicalMeasurement,
                  measurementLabel: activeMeasurement.measurementLabel,
                  primaryRole: primary?.role,
                }
              : {};

          dispatch(
            addSpectralDopplerResult({
              annotationUID: completedAnnotation.annotationUID,
              dicomUuid,
              frameIndex: currentFrame,
              toolVariant: spectralVariant,
              pixelPoints: data.spectralPixelPoints ?? [],
              calibration: data.spectralCalibration,
              values: data.spectralValues,
              ...primaryBinding,
            })
          );

          if (
            primary &&
            activeMeasurement &&
            measurementStatus === 'awaiting' &&
            activeMeasurement.toolType?.startsWith('spectral-doppler-')
          ) {
            value = primary.value;
            // Probe persists as a 1-point POINT, segment/VTI as 2-point LINEs.
            // A click-only segment/VTI yields one handle point; LINE requires
            // exactly 2, so pad a degenerate line rather than dropping it.
            const spectralType = getAnnotationTypeForToolType(
              activeMeasurement.toolType
            );
            const spectralPoints = buildAnnotationPoints(
              completedAnnotation.data
            );
            if (spectralType === 'LINE' && spectralPoints.length === 1) {
              spectralPoints.push(spectralPoints[0]);
            }
            dispatch(
              completeMeasurement({
                value,
                annotationMeta: completedAnnotation.annotationUID
                  ? guardAnnotationMeta({
                      annotationUID: completedAnnotation.annotationUID,
                      type: spectralType,
                      dicomUuid,
                      frameNumber: currentFrame + 1,
                      points: spectralPoints,
                    })
                  : undefined,
              })
            );
          }
        }
        return;
      }

      // Handle SIMPSON measurements (LVdv, LVsv, LA volume ONLY)
      // Process Simpson FIRST before checking cachedStats since it uses contour points
      if (
        activeMeasurement.toolType === 'simpson' &&
        toolName === 'PlanarFreehandROI'
      ) {
        // Kept: the overlay draws its disks and a reshape recalculates them.
        value = recordSimpsonVolume(completedAnnotation, {
          reportMeasurementClassUuid: activeMeasurement.canonicalMeasurement,
          measurementLabel: activeMeasurement.measurementLabel,
        });
      } else {
        const { cachedStats } = completedAnnotation.data;
        if (!cachedStats || Object.keys(cachedStats).length === 0) {
          // Stats not yet computed — store the UID so ANNOTATION_MODIFIED can pick it up
          pendingAnnotationRef.current = completedAnnotation.annotationUID;
          return;
        }

        const targetIds = Object.keys(cachedStats);
        if (targetIds.length === 0) return;

        const stats = cachedStats[targetIds[0]];

        // Handle LENGTH measurements
        if (activeMeasurement.toolType === 'length' && toolName === 'Length') {
          // Cornerstone returns length in mm
          if (stats.length !== undefined && !Number.isNaN(stats.length)) {
            const normalizedUnit = normalizeUnit(
              activeMeasurement.unit ?? 'cm'
            );
            if (normalizedUnit === 'cm') {
              value = stats.length / 10; // mm to cm
            } else {
              value = stats.length; // Keep as mm (no conversion)
            }
          }
        }
        // Handle AREA measurements
        else if (
          activeMeasurement.toolType === 'area' &&
          toolName === 'PlanarFreehandROI'
        ) {
          // Cornerstone returns area in mm²
          if (stats.area !== undefined && !Number.isNaN(stats.area)) {
            const normalizedUnit = normalizeUnit(
              activeMeasurement.unit ?? 'cm2'
            );
            if (normalizedUnit === 'cm2') {
              value = stats.area / 100; // mm² to cm²
            } else {
              value = stats.area; // Keep as mm² (no conversion)
            }
          }
        }
        // Handle ANGLE measurements
        else if (
          activeMeasurement.toolType === 'angle' &&
          toolName === 'Angle'
        ) {
          // Cornerstone returns angle in degrees — no conversion needed
          if (stats.angle !== undefined && !Number.isNaN(stats.angle)) {
            value = stats.angle;
          }
        }
      }

      if (value !== null) {
        pendingAnnotationRef.current = null;
        // Store full-precision value — rounding happens at display time
        // via MeasurementRow using the template's precision field
        const annotationType = getAnnotationTypeForToolType(
          activeMeasurement.toolType
        );
        dispatch(
          completeMeasurement({
            value,
            annotationMeta:
              completedAnnotation.annotationUID && dicomUuid
                ? guardAnnotationMeta({
                    annotationUID: completedAnnotation.annotationUID,
                    type: annotationType,
                    dicomUuid,
                    frameNumber: currentFrame + 1, // 1-based for Cornerstone imageId matching
                    points: buildAnnotationPoints(completedAnnotation.data),
                  })
                : undefined,
          })
        );

        // Re-measure replaces: a new drawing for this measurement drops the
        // prior annotation (and its Simpson overlay) so the old value/contour
        // isn't left behind alongside the new one.
        const canonical = activeMeasurement.canonicalMeasurement;
        Object.entries(annotationToMeasurement).forEach(([uid, mapped]) => {
          if (
            uid !== completedAnnotation.annotationUID &&
            mapped === canonical
          ) {
            try {
              csAnnotation.state.removeAnnotation(uid);
            } catch {
              // Annotation already gone
            }
            dispatch(removeSimpsonResult(uid));
            dispatch(removeSpectralDopplerResult(uid));
          }
        });
        // Repaint so the removed prior annotation clears from its pane.
        triggerAllViewports();

        // Annotations persist on the viewport after capture so the user
        // can see, select, and delete them.
      } else {
        // eslint-disable-next-line no-console -- Debug logging for medical measurements
        console.warn('[MeasurementCapture] No value calculated');
      }
    },
    [
      activeMeasurement,
      measurementStatus,
      dispatch,
      currentFrame,
      dicomUuid,
      annotationToMeasurement,
      canonicalUnitByUuid,
      activeTool,
      recordSimpsonVolume,
    ]
  );

  // Handle recalculation when existing Simpson annotations are modified,
  // AND fallback capture when ANNOTATION_COMPLETED fired before stats were ready.
  const handleAnnotationModified = useCallback(
    (event: Event) => {
      const customEvent = event as CustomEvent<{
        annotation: AnnotationCompletedEventDetail['annotation'];
        changeType?: string;
        viewportId?: string;
      }>;
      const { annotation: modifiedAnnotation, changeType } = customEvent.detail;

      if (!modifiedAnnotation?.annotationUID) return;
      // AP-773: a restore repaints via a synthetic modify — not a reader edit.
      if (isApplyingHistory()) return;
      // Every pane listens globally; only the pane the edit happened in handles
      // it, so volumes use that image's pixel spacing (synthetic events carry
      // no viewportId and still reach every pane).
      const sourceViewportId = customEvent.detail.viewportId;
      if (sourceViewportId && sourceViewportId !== viewportId) return;
      dragSnapshotRef.current?.modified.add(modifiedAnnotation.annotationUID);

      // --- AI volume adopt (freehand): editing an AI contour takes reader
      // ownership. Handle it here on ANY changeType — Cornerstone reports a
      // freehand reshape as a handles OR a stats update, and the Simpson-recalc
      // path below skips StatsUpdated, so relying on it made the adopt miss the
      // reshape (needing an extra click). A polyline-signature guard keeps no-op
      // modifies (hover / select / textbox) from triggering it. Recompute the
      // volume + refresh the disk overlay; the AI→manual flip defers to
      // mouse-up (handleMouseUp) so we don't fight the edit loop. ---
      if (
        (modifiedAnnotation.data as { isAI?: boolean } | undefined)?.isAI ===
          true &&
        modifiedAnnotation.metadata?.toolName === 'PlanarFreehandROI'
      ) {
        const adoptCanonical =
          annotationToMeasurement[modifiedAnnotation.annotationUID];
        const existingSimpson =
          simpsonResults[modifiedAnnotation.annotationUID];
        // No simpsonStats means an area contour, matching the resize path below.
        const areaData = modifiedAnnotation.data as {
          simpsonStats?: unknown;
          polylineSig?: string;
          cachedStats?: Record<string, { area?: number }>;
        };
        if (adoptCanonical && !areaData.simpsonStats) {
          const contourPoints = getContourPointsFromAnnotation(
            modifiedAnnotation as unknown as CornerstoneAnnotation
          );
          const sumX = contourPoints.reduce((acc, p) => acc + p.x, 0);
          const sumY = contourPoints.reduce((acc, p) => acc + p.y, 0);
          const sig = `${contourPoints.length}|${sumX.toFixed(2)}|${sumY.toFixed(2)}`;
          const areaMm2 = Object.values(areaData.cachedStats ?? {})[0]?.area;
          if (
            sig !== areaData.polylineSig &&
            areaMm2 != null &&
            !Number.isNaN(areaMm2)
          ) {
            areaData.polylineSig = sig;
            pendingAdoptRef.current = {
              oldUID: modifiedAnnotation.annotationUID,
              canonicalMeasurement: adoptCanonical,
              value: areaMm2 / 100, // mm² → cm²
              unit: 'cm2',
              points: buildAnnotationPoints(modifiedAnnotation.data),
              type: 'FREEHAND',
            };
            if (!mouseDownRef.current) applyPendingAdoptRef.current();
          }
          return;
        }
        if (adoptCanonical) {
          try {
            const contourPoints = getContourPointsFromAnnotation(
              modifiedAnnotation as unknown as CornerstoneAnnotation
            );
            if (contourPoints.length >= 10) {
              const sumX = contourPoints.reduce((acc, p) => acc + p.x, 0);
              const sumY = contourPoints.reduce((acc, p) => acc + p.y, 0);
              const sig = `${contourPoints.length}|${sumX.toFixed(2)}|${sumY.toFixed(2)}`;
              const prevSig = (
                modifiedAnnotation.data as {
                  simpsonStats?: { polylineSig?: string };
                }
              ).simpsonStats?.polylineSig;
              if (prevSig !== sig) {
                const simpsonResult = calculateSimpsonVolume(contourPoints, {
                  numberOfDisks: 20,
                  pixelSpacing: pixelSpacing ?? 1,
                });
                writeSimpsonStatsToAnnotation(
                  modifiedAnnotation,
                  simpsonResult
                );
                const stats = (
                  modifiedAnnotation.data as {
                    simpsonStats?: Record<string, unknown>;
                  }
                ).simpsonStats;
                if (stats) stats.polylineSig = sig;
                if (existingSimpson) {
                  dispatch(
                    addSimpsonResult({
                      ...simpsonResult,
                      annotationUID: modifiedAnnotation.annotationUID,
                      frameIndex: existingSimpson.frameIndex,
                      dicomUuid: existingSimpson.dicomUuid,
                      reportMeasurementClassUuid:
                        existingSimpson.reportMeasurementClassUuid,
                      measurementLabel: existingSimpson.measurementLabel,
                    })
                  );
                }
                pendingAdoptRef.current = {
                  oldUID: modifiedAnnotation.annotationUID,
                  canonicalMeasurement: adoptCanonical,
                  value: simpsonResult.volume,
                  unit: simpsonResult.volumeUnit,
                  points: buildAnnotationPoints(modifiedAnnotation.data),
                  type: 'FREEHAND',
                };
                // Reshape events can arrive after the mouse is released
                // (freehand tools finalize late). If so, the mouseup already
                // ran — apply the adopt now instead of waiting for another.
                if (!mouseDownRef.current) applyPendingAdoptRef.current();
              }
            }
          } catch (error) {
            // eslint-disable-next-line no-console -- medical measurement recompute
            console.error(
              '[MeasurementCapture] AI volume adopt recompute failed:',
              error
            );
          }
        }
        return;
      }

      // --- Fallback: capture stats that arrived after ANNOTATION_COMPLETED ---
      if (
        pendingAnnotationRef.current === modifiedAnnotation.annotationUID &&
        changeType === 'StatsUpdated' &&
        activeMeasurement &&
        measurementStatus === 'awaiting'
      ) {
        pendingAnnotationRef.current = null;

        const { cachedStats } = modifiedAnnotation.data ?? {};
        if (!cachedStats) return;

        const targetIds = Object.keys(cachedStats);
        if (targetIds.length === 0) return;

        const stats = cachedStats[targetIds[0]];
        let value: number | null = null;

        if (activeMeasurement.toolType === 'area' && stats.area != null) {
          const normalizedUnit = normalizeUnit(activeMeasurement.unit ?? 'cm2');
          value = normalizedUnit === 'cm2' ? stats.area / 100 : stats.area;
        } else if (
          activeMeasurement.toolType === 'length' &&
          stats.length != null
        ) {
          const normalizedUnit = normalizeUnit(activeMeasurement.unit ?? 'cm');
          value = normalizedUnit === 'cm' ? stats.length / 10 : stats.length;
        } else if (
          activeMeasurement.toolType === 'angle' &&
          stats.angle != null
        ) {
          value = stats.angle;
        }

        if (value !== null && !Number.isNaN(value)) {
          // AP-676: read the tool's real geometry source — raw handles.points
          // is empty for a freehand and 400s the report save.
          const modAnnotationType = getAnnotationTypeForToolType(
            activeMeasurement.toolType
          );
          dispatch(
            completeMeasurement({
              value,
              annotationMeta:
                modifiedAnnotation.annotationUID && dicomUuid
                  ? guardAnnotationMeta({
                      annotationUID: modifiedAnnotation.annotationUID,
                      type: modAnnotationType,
                      dicomUuid,
                      frameNumber: currentFrame + 1, // 1-based for Cornerstone imageId matching
                      points: buildAnnotationPoints(modifiedAnnotation.data),
                    })
                  : undefined,
            })
          );
          // Annotation persists on the viewport after capture.
        }
        return;
      }

      // --- Spectral Doppler recalculation on annotation drag/reshape ---
      // The tool's renderAnnotation sets updated data.spectralValues on
      // invalidation, then fires StatsUpdated. We (a) mirror the values to any
      // existing Redux result and (b) fold the primary value into the report so
      // a HYDRATED probe/VTI behaves like a hydrated Length: dragging updates
      // the worksheet value and — for an AI point — adopts it to manual (blue).
      const spectralVariant = modifiedAnnotation.metadata?.toolName
        ? SPECTRAL_TOOL_NAME_TO_VARIANT[modifiedAnnotation.metadata.toolName]
        : undefined;
      if (spectralVariant && changeType === 'StatsUpdated') {
        const data = modifiedAnnotation.data as unknown as {
          spectralValues?: SpectralDopplerValues;
          spectralCalibration?: SpectralCalibration;
          spectralPixelPoints?: [number, number][];
          isAI?: boolean;
        };
        const modifiedUID = modifiedAnnotation.annotationUID;

        // Keep an existing Redux result (fresh-draw path) in sync.
        const existingSpectralResult = spectralDopplerResults[modifiedUID];
        if (
          existingSpectralResult &&
          data?.spectralValues &&
          data?.spectralCalibration
        ) {
          dispatch(
            addSpectralDopplerResult({
              ...existingSpectralResult,
              calibration: data.spectralCalibration,
              values: data.spectralValues,
              pixelPoints:
                data.spectralPixelPoints ?? existingSpectralResult.pixelPoints,
            })
          );
        }

        // Dirty the worksheet like Length/Angle: the auto-populate effect bails
        // on a pristine report, so route the primary value through
        // updateHydratedMeasurement (human) or the adopt stash (AI point).
        const canonicalMeasurement = annotationToMeasurement[modifiedUID];
        const primary = data?.spectralValues
          ? extractPrimaryValue(
              spectralVariant,
              data.spectralValues,
              canonicalUnitByUuid.get(canonicalMeasurement)
            )
          : null;
        if (canonicalMeasurement && primary) {
          const { unit } = primary;
          // Probe persists as a POINT; segment/VTI as LINEs.
          const type: 'POINT' | 'LINE' =
            spectralVariant === 'probe' ? 'POINT' : 'LINE';
          const points = buildAnnotationPoints(modifiedAnnotation.data);

          if (data?.isAI === true) {
            // Reader took ownership by dragging → flip to manual on mouse-up.
            pendingAdoptRef.current = {
              oldUID: modifiedUID,
              canonicalMeasurement,
              value: primary.value,
              unit,
              points,
              ...(type === 'POINT' ? { type: 'POINT' as const } : {}),
            };
            // The recompute event can land after the mouse is already up — if so
            // apply now instead of waiting for another interaction.
            if (!mouseDownRef.current) applyPendingAdoptRef.current();
          } else {
            dispatch(
              updateHydratedMeasurement({
                annotationUID: modifiedUID,
                canonicalMeasurement,
                value: primary.value,
                unit,
                points,
                ...(type === 'POINT' ? { type: 'POINT' as const } : {}),
              })
            );
          }
        }
        return;
      }

      // --- Simpson recalculation on annotation drag/reshape ---
      // Check if this is a Simpson annotation by looking up in simpsonResults
      const existingResult = simpsonResults[modifiedAnnotation.annotationUID];
      if (existingResult) {
        // Skip internal Cornerstone stat updates to avoid unnecessary recalculations
        if (changeType === 'StatsUpdated' || changeType === 'InitialSetup')
          return;

        // Verify it's a PlanarFreehandROI (Simpson tool)
        if (modifiedAnnotation.metadata?.toolName !== 'PlanarFreehandROI')
          return;

        try {
          const contourPoints = getContourPointsFromAnnotation(
            modifiedAnnotation as unknown as CornerstoneAnnotation
          );
          if (contourPoints.length >= 10) {
            // Short-circuit no-op modifies. ANNOTATION_MODIFIED also fires on
            // hover / textbox move / selection re-renders — without this, an AI
            // volume would adopt-to-manual (turn blue) just from hovering it.
            // Same polyline-signature scheme as the linear/hydrated path.
            const sumX = contourPoints.reduce((acc, p) => acc + p.x, 0);
            const sumY = contourPoints.reduce((acc, p) => acc + p.y, 0);
            const polylineSig = `${contourPoints.length}|${sumX.toFixed(2)}|${sumY.toFixed(2)}`;
            const prevSig = (
              modifiedAnnotation.data as
                | { simpsonStats?: { polylineSig?: string } }
                | undefined
            )?.simpsonStats?.polylineSig;
            if (prevSig !== undefined && polylineSig === prevSig) return;

            const simpsonResult = calculateSimpsonVolume(contourPoints, {
              numberOfDisks: 20,
              pixelSpacing: pixelSpacing ?? 1,
            });

            // Keep the annotation's simpsonStats in sync with the reshape so
            // the draggable text box re-renders the updated Vol + L.
            writeSimpsonStatsToAnnotation(modifiedAnnotation, simpsonResult);
            // Re-seed the signature (writeSimpsonStatsToAnnotation replaces the
            // simpsonStats object) so the next no-op modify is short-circuited.
            const nextStats = (
              modifiedAnnotation.data as {
                simpsonStats?: Record<string, unknown>;
              }
            ).simpsonStats;
            if (nextStats) nextStats.polylineSig = polylineSig;

            // Update the existing result, preserving frameIndex, dicomUuid and report field link
            dispatch(
              addSimpsonResult({
                ...simpsonResult,
                annotationUID: modifiedAnnotation.annotationUID,
                frameIndex: existingResult.frameIndex,
                dicomUuid: existingResult.dicomUuid,
                reportMeasurementClassUuid:
                  existingResult.reportMeasurementClassUuid,
                measurementLabel: existingResult.measurementLabel,
              })
            );

            // Route the new volume through updateHydratedMeasurement — the only
            // path that writes a reshaped volume into its row (as for Length /
            // Angle); Simpson results themselves are never folded into rows.
            if (existingResult.reportMeasurementClassUuid) {
              // Human volume resize (AI volumes are adopted at the top of this
              // handler and never reach here) → fold the new volume into the
              // report so the editor dirties.
              dispatch(
                updateHydratedMeasurement({
                  annotationUID: modifiedAnnotation.annotationUID,
                  canonicalMeasurement:
                    existingResult.reportMeasurementClassUuid,
                  value: simpsonResult.volume,
                  unit: simpsonResult.volumeUnit,
                  points: buildAnnotationPoints(modifiedAnnotation.data),
                  // Simpson contours persist as FREEHAND, not the LINE default.
                  type: 'FREEHAND',
                  dicomUuid: existingResult.dicomUuid,
                  frameNumber: (existingResult.frameIndex ?? 0) + 1,
                })
              );
            }
          }
        } catch (error) {
          // eslint-disable-next-line no-console -- Error logging for medical measurements
          console.error(
            '[MeasurementCapture] Error recalculating Simpson:',
            error
          );
        }
        return;
      }

      // --- Hydrated annotation resized/moved: update worksheet value ---
      const canonicalMeasurement =
        annotationToMeasurement[modifiedAnnotation.annotationUID];
      if (!canonicalMeasurement) return;
      if (changeType !== 'StatsUpdated') return;

      const { cachedStats } = modifiedAnnotation.data ?? {};
      if (!cachedStats) return;

      const targetIds = Object.keys(cachedStats);
      if (targetIds.length === 0) return;

      const stats = cachedStats[targetIds[0]];
      const toolName = modifiedAnnotation.metadata?.toolName;
      let value: number | null = null;
      let unit = 'cm';

      if (toolName === 'Length' && stats.length != null) {
        value = stats.length / 10; // mm → cm
        unit = 'cm';
      } else if (toolName === 'Angle' && stats.angle != null) {
        value = stats.angle;
        unit = 'deg';
      } else if (toolName === 'PlanarFreehandROI' && stats.area != null) {
        // Volume freehand (AI hydrated or adopted): recompute Simpson volume
        // from the edited polyline so the worksheet tracks the reshape, not
        // the raw area. The marker is `data.simpsonStats` — set at hydration
        // and preserved through the AI→human adopt UID conversion — so this
        // path stays selected across subsequent edits.
        const data = modifiedAnnotation.data as
          | Record<string, unknown>
          | undefined;
        const existingStats = data?.simpsonStats as
          | { polylineSig?: string }
          | undefined;
        if (existingStats) {
          try {
            const contourPoints = getContourPointsFromAnnotation(
              modifiedAnnotation as unknown as CornerstoneAnnotation
            );
            // Polyline signature: short-circuit when the contour hasn't
            // changed. ANNOTATION_MODIFIED also fires on textbox click/drag;
            // re-running calculateSimpsonVolume there would overwrite
            // encephalon's mL value with a pixel-scale result (video frames
            // carry no DICOM pixel spacing).
            const sumX = contourPoints.reduce((acc, p) => acc + p.x, 0);
            const sumY = contourPoints.reduce((acc, p) => acc + p.y, 0);
            const polylineSig = `${contourPoints.length}|${sumX.toFixed(2)}|${sumY.toFixed(2)}`;
            if (polylineSig === existingStats.polylineSig) {
              return;
            }
            if (contourPoints.length >= 10) {
              const simpsonResult = calculateSimpsonVolume(contourPoints, {
                numberOfDisks: 20,
                pixelSpacing: pixelSpacing ?? 1,
              });
              writeSimpsonStatsToAnnotation(modifiedAnnotation, simpsonResult);
              const updatedStats = (
                modifiedAnnotation.data as Record<string, unknown>
              ).simpsonStats as Record<string, unknown>;
              updatedStats.polylineSig = polylineSig;
              value = simpsonResult.volume;
              unit = simpsonResult.volumeUnit;
            }
          } catch (error) {
            // eslint-disable-next-line no-console -- Error logging for medical measurements
            console.error(
              '[MeasurementCapture] Error recomputing Simpson volume:',
              error
            );
          }
        } else {
          value = stats.area / 100; // mm² → cm²
          unit = 'cm2';
        }
      }

      if (value !== null && !Number.isNaN(value)) {
        const isFreehand = hasContourGeometry(modifiedAnnotation.data);
        const points = buildAnnotationPoints(modifiedAnnotation.data);

        const modifiedUID = modifiedAnnotation.annotationUID;
        // Adopt keys off the live `isAI` flag, NOT the UID prefix: adoption is
        // done in place (the UID never changes), so once a line has been
        // adopted its `isAI` is false and further drags take the resize path
        // below. Using the prefix would re-trigger adoption on every edit.
        const isAdopt =
          (modifiedAnnotation.data as { isAI?: boolean } | undefined)?.isAI ===
          true;

        if (isAdopt) {
          // Stash the latest drag state; the ownership flip happens on
          // MOUSE_UP so we don't fight Cornerstone's drag loop.
          pendingAdoptRef.current = {
            oldUID: modifiedUID,
            canonicalMeasurement,
            value,
            unit,
            points,
            ...(isFreehand ? { type: 'FREEHAND' as const } : {}),
          };
          return;
        }

        dispatch(
          updateHydratedMeasurement({
            annotationUID: modifiedUID,
            canonicalMeasurement,
            value,
            unit,
            points,
            // Hydrated lines keep their existing row type; freehand must flip it.
            ...(isFreehand ? { type: 'FREEHAND' as const } : {}),
          })
        );
      }
    },
    [
      simpsonResults,
      spectralDopplerResults,
      annotationToMeasurement,
      dispatch,
      pixelSpacing,
      activeMeasurement,
      measurementStatus,
      currentFrame,
      dicomUuid,
      canonicalUnitByUuid,
      viewportId,
    ]
  );

  // Finalize a pending AI adoption when the user releases the mouse.
  // Runs once at drag-end so we don't fight Cornerstone's drag loop.
  //
  // IMPORTANT: adoption is done IN PLACE — we mutate the existing annotation's
  // data (isAI → false, strip the "AI:" label) and keep the same annotationUID
  // and Cornerstone annotation object. An earlier version removed the AI
  // annotation and re-added a converted copy under a `hydrated-*` UID; that
  // silently broke re-editing because the re-added plain object loses the
  // LengthTool's interaction wiring (it renders and highlights, but its
  // endpoints can no longer be grabbed — no ANNOTATION_MODIFIED ever fires).
  // Adopt detection keys off `data.isAI`, not the UID prefix, so a subsequent
  // drag of the now-human line takes the normal resize path.
  const applyPendingAdopt = useCallback(() => {
    const pending = pendingAdoptRef.current;
    if (!pending) return;
    pendingAdoptRef.current = null;

    try {
      const ann = csAnnotation.state.getAnnotation(pending.oldUID);
      if (ann) {
        const data = (ann.data ?? {}) as Record<string, unknown>;
        // Take reader ownership: no longer an AI measurement, and strip the
        // "AI:" prefix from the on-image/worksheet label.
        data.isAI = false;
        if (typeof data.measurementLabel === 'string') {
          data.measurementLabel = data.measurementLabel.replace(/^AI:\s*/, '');
        }
        // Keep it unlocked so it stays editable (canEdit is implied — the user
        // just dragged it).
        csAnnotation.locking.setAnnotationLocked(pending.oldUID, false);

        // Re-skin to the human (blue) palette immediately. Preserve the
        // selected variant if this line is the selected one, then re-render so
        // the colour flips now rather than on the next interaction.
        const isSelected = selectedAnnotationUIDRef.current === pending.oldUID;
        csAnnotation.config.style.setAnnotationStyles(
          pending.oldUID,
          getBaseAnnotationStyle(ann, { isSelected })
        );
        // A freehand flip is a plain data mutation, so nothing invalidated the
        // cached contour or fired an event — the contour + header chip would
        // stay mint until the next interaction (linear repaints fine on its own
        // drag-end event). Mark it invalidated + re-render so the contour
        // recolors, and emit a synthetic ANNOTATION_MODIFIED so the header chip
        // (SelectedAnnotationLabel ticks on that event) reflects the new
        // colour/label immediately. `StatsUpdated` is a no-op in the modified
        // handler above (the freehand path returns early on it), so it won't
        // re-trigger adoption.
        const isFreehand = pending.type === 'FREEHAND';
        // A probe (POINT) flip is likewise a plain data mutation — the crosshair
        // recolors on the next render (getAnnotationStyle re-reads the style),
        // but the header chip only ticks on ANNOTATION_MODIFIED, so emit the
        // same synthetic event to flip its AI→manual background immediately.
        const needsRepaintEvent = isFreehand || pending.type === 'POINT';
        if (isFreehand) {
          (ann as { invalidated?: boolean }).invalidated = true;
        }
        const enabledElement = viewportId
          ? getEnabledElementByViewportId(viewportId)
          : undefined;
        enabledElement?.viewport
          ?.getRenderingEngine?.()
          ?.renderViewports([viewportId as string]);
        if (needsRepaintEvent) {
          triggerEvent(eventTarget, Enums.Events.ANNOTATION_MODIFIED, {
            annotation: ann,
            changeType: 'StatsUpdated',
          });
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[adopt] failed to convert AI annotation on mouse up:', err);
    }

    // The annotation→canonical mapping was set at hydration under this same
    // UID, so it already resolves; just fold the new value/geometry into the
    // report (creates the human measurement + soft-deletes the AI source in
    // useMeasurementAutoPopulate).
    dispatch(
      updateHydratedMeasurement({
        annotationUID: pending.oldUID,
        canonicalMeasurement: pending.canonicalMeasurement,
        value: pending.value,
        unit: pending.unit,
        points: pending.points,
        // Volume/area contours must fold in as FREEHAND, not the LINE default.
        ...(pending.type ? { type: pending.type } : {}),
      })
    );
  }, [dispatch, viewportId]);

  // Keep the ref pointed at the latest callback so the modified handler (defined
  // above) can apply a late-arriving adopt without depending on it.
  useEffect(() => {
    applyPendingAdoptRef.current = applyPendingAdopt;
  }, [applyPendingAdopt]);

  // AP-773: commit the drag's history step. Runs one macrotask after our
  // document mouseup: Cornerstone's own mouseup listener (registered at
  // mousedown, so it fires after ours) is where the freehand edit loop writes
  // the final contour and fires ANNOTATION_MODIFIED — committing synchronously
  // would snapshot a contour edit that has not landed yet and, for an
  // already-human volume, see no change at all and record nothing.
  const commitDrag = useCallback(
    (drag: NonNullable<typeof dragSnapshotRef.current>) => {
      if (!drag.owner || !viewportId) return;
      const location = dicomUuid
        ? { viewportId, dicomUuid, frame: currentFrame }
        : undefined;
      // A1: an annotation completed during this drag that did not exist at
      // mousedown. One draw = one step (a Length's first click leaves an
      // in-progress annotation behind; it is committed at the completing click).
      // A contour EDIT also ends with ANNOTATION_COMPLETED on the existing
      // annotation (PlanarFreehandROI's edit loops), so "completed" alone does
      // not mean "created": only uids absent from the mousedown snapshot are.
      const created = new Set(
        [...drag.completed].filter((uid) => !drag.annotations.has(uid))
      );
      created.forEach((uid) => {
        const clone = cloneAnnotation(uid);
        if (!clone) return;
        commitHistoryStep(
          createCreateStep({
            clone,
            worksheetBefore: drag.worksheet,
            resultsBefore: snapshotSessionResults(drag.reduxState, uid),
            store: reduxStore,
            location,
          })
        );
      });
      // A2: everything else touched during the drag — modified, or completed
      // by an edit loop — that changed geometry or derived results.
      new Set([...drag.modified, ...drag.completed]).forEach((uid) => {
        if (created.has(uid)) return;
        const before = drag.annotations.get(uid);
        const live = csAnnotation.state.getAnnotation(uid);
        const after = snapshotAnnotationData(live?.data);
        const resultsBefore: SessionResultsSnapshot = snapshotSessionResults(
          drag.reduxState,
          uid
        );
        const resultsAfter: SessionResultsSnapshot = snapshotSessionResults(
          reduxStore.getState(),
          uid
        );
        // ANNOTATION_MODIFIED also fires on hover / select; only a real
        // geometry (or derived-result) change is an A2 action.
        if (
          !before ||
          !after ||
          (sameAnnotationData(before, after) &&
            sameSessionResults(resultsBefore, resultsAfter))
        ) {
          return;
        }
        commitHistoryStep(
          createReshapeStep({
            annotationUID: uid,
            before,
            after,
            worksheetBefore: drag.worksheet,
            resultsBefore,
            resultsAfter,
            dispatch,
            location,
            label: `${annotationName(live?.data as Record<string, unknown>)} reshaped`,
          })
        );
      });
    },
    [viewportId, dicomUuid, currentFrame, reduxStore, dispatch]
  );
  const commitDragRef = useRef(commitDrag);
  useEffect(() => {
    commitDragRef.current = commitDrag;
  }, [commitDrag]);

  /** Commit a drag whose deferred commit has not fired yet (next mousedown,
   *  unmount) so no step is lost and snapshots never overlap. */
  const flushPendingDrag = useCallback(() => {
    const drag = dragSnapshotRef.current;
    if (!drag?.commitTimer) return;
    clearTimeout(drag.commitTimer);
    dragSnapshotRef.current = null;
    commitDragRef.current(drag);
  }, []);

  const handleMouseDown = useCallback(() => {
    mouseDownRef.current = true;
    flushPendingDrag();
    dragSnapshotRef.current = {
      owner: !!viewportId && activeViewportId === viewportId,
      annotations: snapshotAllAnnotations(),
      worksheet: getWorksheetSnapshot(),
      reduxState: reduxStore.getState(),
      modified: new Set(),
      completed: new Set(),
      commitTimer: null,
    };
  }, [activeViewportId, viewportId, reduxStore, flushPendingDrag]);

  const handleMouseUp = useCallback(() => {
    mouseDownRef.current = false;
    applyPendingAdopt();

    const drag = dragSnapshotRef.current;
    if (!drag || drag.commitTimer) return;
    drag.commitTimer = setTimeout(() => {
      if (dragSnapshotRef.current === drag) dragSnapshotRef.current = null;
      commitDragRef.current(drag);
    }, 0);
  }, [applyPendingAdopt]);

  // Flush a pending commit on unmount so a step is never dropped.
  useEffect(() => () => flushPendingDrag(), [flushPendingDrag]);

  // The document mouse listeners are registered ONCE per viewport, in the
  // capture phase, and call through refs. Re-registering them from the main
  // listener effect (whose deps change on every Redux update a drag causes)
  // let a mouseup be dropped: Cornerstone adds its own document mouseup at
  // mousedown; if ours was re-added after it, Cornerstone's handler ran
  // first, its completion dispatched, React re-ran the effect, and our
  // listener was removed mid-dispatch — never called. Capture phase also
  // guarantees our snapshot/commit ordering relative to Cornerstone's handlers.
  const mouseDownHandlerRef = useRef(handleMouseDown);
  const mouseUpHandlerRef = useRef(handleMouseUp);
  useEffect(() => {
    mouseDownHandlerRef.current = handleMouseDown;
    mouseUpHandlerRef.current = handleMouseUp;
  }, [handleMouseDown, handleMouseUp]);
  useEffect(() => {
    if (!viewportId) return undefined;
    const onDown = () => mouseDownHandlerRef.current();
    const onUp = () => mouseUpHandlerRef.current();
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('mouseup', onUp, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('mouseup', onUp, true);
    };
  }, [viewportId]);

  useEffect(() => {
    if (!viewportId) return () => {};

    eventTarget.addEventListener(
      Enums.Events.ANNOTATION_COMPLETED,
      handleAnnotationCompleted
    );
    eventTarget.addEventListener(
      Enums.Events.ANNOTATION_MODIFIED,
      handleAnnotationModified
    );

    return () => {
      eventTarget.removeEventListener(
        Enums.Events.ANNOTATION_COMPLETED,
        handleAnnotationCompleted
      );
      eventTarget.removeEventListener(
        Enums.Events.ANNOTATION_MODIFIED,
        handleAnnotationModified
      );
    };
  }, [viewportId, handleAnnotationCompleted, handleAnnotationModified]);
}
